import { DataType } from '@mode-switch/express';
import * as angular from 'angular';

import { SelectedChartService } from '@mode/charts/contract';
import { FieldFormatHelpers } from '@mode/charts/util-vegas';
import { ChartTypes } from '@mode/shared/contract-common';
import { DECIMAL_MAX, getAllowedFormatTypesByCol, textFormatOptions } from '@mode/shared/util-js';

const NUMERIC_TYPE = [ChartTypes.FormatType.Financial, ChartTypes.FormatType.Number, ChartTypes.FormatType.Percent];
const NON_CURRENCY = [ChartTypes.FormatType.Number, ChartTypes.FormatType.Percent];

class TextFormatPanelController implements angular.IController {
  static $inject = [];

  formatChanged: (args: { $format: ChartTypes.TextFormat }) => void;
  hideDisabled: 'true' | 'false' | undefined;

  availableFormatTypes: { value: ChartTypes.FormatType; label: string }[];
  options = textFormatOptions;
  fieldType: DataType.Name;

  textFormat: ChartTypes.TextFormat;
  mod: ChartTypes.TextFormat; // For use in binding

  chartService: SelectedChartService;

  // Since these will be called every digest, materialize them
  showThousandsSeparator: boolean;
  showDigitGrouping: boolean;
  showDecimalPlaces: boolean;
  showDecimalStyle: boolean;
  showNegativeBehavior: boolean;
  showCurrencyPrefix: boolean;
  showCurrencySuffix: boolean;

  constructor() {}

  $onChanges(changes: angular.IOnChangesObject) {
    if (changes.textFormat && this.textFormat != null) {
      this.mod = {
        ...FieldFormatHelpers.makeDefaultTextFormat(), // Easy way to set defaults for dropdowns
        ...this.textFormat,
      };
      this.setVisibleFields();
    }

    if (changes.fieldType && this.fieldType != null) {
      this.availableFormatTypes = getAllowedFormatTypesByCol(this.fieldType).filter((type) =>
        this.hideDisabled === 'true' ? !type.disabled : true
      );
      this.setVisibleFields();
    }
  }

  setVisibleFields() {
    const isFinancial = this.mod.type === ChartTypes.FormatType.Financial;
    const isCustomCurrency = isFinancial && this.mod.currencyPreset === ChartTypes.CurrencyPreset.Custom;
    this.showThousandsSeparator = NON_CURRENCY.includes(this.mod.type) || isCustomCurrency;
    this.showDigitGrouping = NON_CURRENCY.includes(this.mod.type) || isCustomCurrency;
    this.showDecimalPlaces = NUMERIC_TYPE.includes(this.mod.type);
    this.showDecimalStyle = NUMERIC_TYPE.includes(this.mod.type);
    this.showCurrencyPrefix = isCustomCurrency;
    this.showCurrencySuffix = isCustomCurrency;
    this.showNegativeBehavior = isFinancial;
  }

  updateFormat() {
    this.formatChanged({ $format: this.mod });
  }

  updateCurrencyPreset() {
    this.mod = FieldFormatHelpers.setCurrencyPreset(this.mod, this.mod.currencyPreset);
    this.updateFormat();
  }
}

export const TextFormatPanelComponent: angular.IComponentOptions = {
  bindings: {
    fieldType: '<', // cannot start with the word data due to conflict with data attributes
    textFormat: '<',
    hideDisabled: '@',
    formatChanged: '&',
  },
  controller: TextFormatPanelController,
  controllerAs: 'ctrl',

  template: `
    <div class="format-settings-container">
      <div class="form-field format-type fullwidth">
        <custom-select-dropdown
          ng-if="ctrl.availableFormatTypes != null"
          input-id="format-type"
          input-label="Category"
          ng-model="ctrl.mod.type"
          select-options="ctrl.availableFormatTypes"
          is-small="true"
          ng-change="ctrl.updateFormat()"
        ></custom-select-dropdown>
      </div>

      <div class="form-field fullwidth" ng-if="ctrl.mod.type === '${ChartTypes.FormatType.Financial}'">
        <custom-select-dropdown
          input-id="currency-preset"
          input-label="Presets"
          ng-model="ctrl.mod.currencyPreset"
          select-options="ctrl.options.currencyPresetOptions"
          is-small="true"
          ng-change="ctrl.updateCurrencyPreset()"
        ></custom-select-dropdown>
      </div>
      <!-- TODO: Dates -->
      <!-- NUMBER-RELATED SETTINGS -->
      <div class="format-settings grid">
        <div class="form-field col-1" ng-if="ctrl.showCurrencyPrefix">
          <label class="field-label">Prefix</label>
          <input type="text" class="form-control form-control-sm"
            ng-model="ctrl.mod.currencySymbolPrefix"
            ng-change="ctrl.updateFormat()"
            ng-model-options="{ updateOn: 'blur' }"
          ></input>
        </div>
        <div class="form-field col-1" ng-if="ctrl.showCurrencySuffix">
          <label class="field-label">Suffix</label>
          <input type="text" class="form-control form-control-sm"
            ng-model="ctrl.mod.currencySymbolSuffix"
            ng-change="ctrl.updateFormat()"
            ng-model-options="{ updateOn: 'blur' }"
          ></input>
        </div>
        <div class="form-field col-1" ng-if="ctrl.showThousandsSeparator">
          <label class="field-label">Separator</label>
          <select class="form-control form-control-sm form-control-block"
            ng-change="ctrl.updateFormat()"
            ng-model="ctrl.mod.separator"
            ng-options="sep.value as sep.label for sep in ctrl.options.thousandsSeparatorOptions"
          ></select>
        </div>
        <div class="form-field col-1" ng-if="ctrl.showDigitGrouping">
          <label class="field-label">Grouping</label>
          <select class="form-control form-control-sm form-control-block"
            ng-change="ctrl.updateFormat()"
            ng-model="ctrl.mod.grouping"
            ng-options="grouping.value as grouping.label for grouping in ctrl.options.thousandsGroupingOptions"
          ></select>
        </div>
        <div class="form-field col-1" ng-if="ctrl.showDecimalStyle">
          <label class="field-label">Decimal Style</label>
          <select class="form-control form-control-sm form-control-block"
            ng-change="ctrl.updateFormat()"
            ng-model="ctrl.mod.decimalStyle"
            ng-options="decimal.value as decimal.label for decimal in ctrl.options.decimalSymbolOptions"
          ></select>
        </div>
        <div class="form-field col-1" ng-if="ctrl.showDecimalPlaces">
          <label class="field-label">Places</label>
          <input type="number" max="${DECIMAL_MAX}" min="0" class="form-control form-control-sm"
            placeholder="0-${DECIMAL_MAX}"
            ng-model="ctrl.mod.precision"
            ng-change="ctrl.updateFormat()"
            ng-model-options="{ updateOn: 'blur' }"
          ></input>
        </div>
        <div class="form-field col-1" ng-if="ctrl.showNegativeBehavior">
          <label class="field-label">Negative Style</label>
          <select class="form-control form-control-sm form-control-block"
            ng-change="ctrl.updateFormat()"
            ng-model="ctrl.mod.negativeBehavior"
            ng-options="negBehavior.value as negBehavior.label for negBehavior in ctrl.options.negativeBehaviorOptions"
          ></select>
        </div>
      </div>
    </div>
`,
};
